import { computed, inject, Injectable, signal } from '@angular/core';
import { EmbedContextService, RestService, TranslationService } from 'src/app/services';
import { buildSharedRouteLink } from 'src/app/helpers/share-link';
import { resolveEmbedShareConfig } from 'src/app/config/share-link.config';
import { normalizeLangCode } from '../shared/save-recommeded-route-tech-mode-view/helpers/resolve-localized-name';
import { CustomRouteService } from './custom-route.service';

/**
 * Estado y única fuente del enlace del botón "Compartir" del planner
 * (HRS-2890), antes duplicado en map.component — desktop — y
 * menu-nav.component — móvil.
 *
 * Cascada: `share_base` validado (fuente de verdad, lo manda la web anfitriona
 * en el src del iframe ya resuelto por idioma — aplica tanto al widget del
 * chatbot como al site embebido en modo planner) → en el embed, mapa fallback
 * por idioma del chatbot → URL legacy `<tenant>.ciceroneweb.com` (embeds sin
 * config). En el site sin `share_base` devuelve `undefined` y
 * ShareResourceComponent cae a `window.location.href` (la propia URL ya es la
 * ruta).
 */
@Injectable({ providedIn: 'root' })
export class ShareRouteLinkService {
  private _embedContext = inject(EmbedContextService);
  private _customRouteService = inject(CustomRouteService);
  private _restService = inject(RestService);
  private _translationService = inject(TranslationService);

  /**
   * `share_base` YA validado contra la allowlist global (sanitizeShareBase):
   * URL de la página del planner en la web anfitriona, base del enlace de
   * compartir. Null si no llegó o no validó (→ fallback por idioma en el
   * embed). Lo setea app.component al arrancar — antes de su branch embed,
   * para cubrir tanto el widget del chatbot como el modo planner.
   */
  public readonly shareBaseUrl = signal<string | null>(null);

  /**
   * Token consumido de `?shared_route=` al arrancar el embed: un enlace
   * compartido abierto en la web anfitriona. Permite distinguir el fallo de
   * carga de ESA ruta (→ aviso + vuelta al chat inicial) del de una generada
   * por el chat (comportamiento intacto). Lo setea app.component
   * (`_openSharedRouteIfPresent`, solo en el embed: en el site se navega al
   * deep-link y no hace falta). Se limpia al cargar o fallar.
   */
  public readonly sharedRouteToken = signal<string | null>(null);

  /** Idioma activo, reactivo. */
  private _lang = this._translationService.selectedLanguage;

  public readonly shareRouteUrl = computed<string | undefined>(() => {
    const token = this._customRouteService.token();
    if (!token) return undefined;

    // share_base manda, haya o no chatbot (widget embed y site en modo planner).
    const shareBase = this.shareBaseUrl();
    if (shareBase) return buildSharedRouteLink(shareBase, token);

    // Site sin share_base: la propia URL ya es la ruta (default de share-resource).
    if (!this._embedContext.isEmbed()) return undefined;

    const tenant = this._restService.subDomain;
    if (!tenant) return undefined;

    const config = resolveEmbedShareConfig(this._embedContext.chatbotId());
    if (config) {
      const fallbackUrl =
        config.fallbackShareUrlByLang[normalizeLangCode(this._lang())] ?? config.defaultFallbackShareUrl;
      return buildSharedRouteLink(fallbackUrl, token);
    }

    return `https://${tenant}.ciceroneweb.com/custom-route/v3/${token}`;
  });
}
